"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import SectionTitle from "@/components/SectionTitle"

type PdfDocument = {
  numPages: number
  getPage: (pageNumber: number) => Promise<PdfPage>
}

type PdfPage = {
  getViewport: (params: { scale: number; rotation?: number }) => { width: number; height: number }
  render: (params: {
    canvasContext: CanvasRenderingContext2D
    viewport: { width: number; height: number }
  }) => { promise: Promise<void>; cancel?: () => void }
}

type PdfJs = {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (
    src:
      | string
      | {
          url: string
          disableRange?: boolean
          disableStream?: boolean
          disableAutoFetch?: boolean
        }
  ) => { promise: Promise<PdfDocument> }
}

type PdfPagedCatalogViewerProps = {
  pdfUrl: string
  errorMessage: string
  title?: string
  description?: string
}

function isRenderCancelledError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false
  const maybe = err as { name?: string; message?: string }
  const name = (maybe.name ?? "").toLowerCase()
  const message = (maybe.message ?? "").toLowerCase()
  return name.includes("renderingcancelled") || message.includes("rendering cancelled")
}

export default function PdfPagedCatalogViewer({
  pdfUrl,
  errorMessage,
  title,
  description,
}: PdfPagedCatalogViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const viewerRef = useRef<HTMLDivElement | null>(null)
  const renderTokenRef = useRef(0)
  const renderTaskRef = useRef<{ cancel?: () => void } | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [pdfDoc, setPdfDoc] = useState<PdfDocument | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [rendering, setRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadPdf() {
      try {
        setLoading(true)
        setError(null)

        const importRemote = new Function("u", "return import(u)") as (u: string) => Promise<PdfJs>
        const pdfjs = await importRemote("https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.min.mjs")
        pdfjs.GlobalWorkerOptions.workerSrc =
          "https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs"

        const doc = await pdfjs
          .getDocument({
            url: pdfUrl,
            disableRange: true,
            disableStream: true,
            disableAutoFetch: true,
          })
          .promise

        if (cancelled) return

        setPdfDoc(doc)
        setNumPages(doc.numPages)
        setPageNumber(1)
      } catch {
        if (!cancelled) {
          setError(errorMessage)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadPdf()

    return () => {
      cancelled = true
    }
  }, [errorMessage, pdfUrl])

  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return

    const token = ++renderTokenRef.current
    if (renderTaskRef.current?.cancel) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }

    const canvas = canvasRef.current
    const page = await pdfDoc.getPage(pageNumber)
    const rotation = 0

    const baseViewport = page.getViewport({ scale: 1, rotation })
    const containerWidth = viewerRef.current?.clientWidth ?? window.innerWidth
    const maxWidth = Math.min(Math.max(containerWidth - 24, 320), isFullscreen ? 2200 : 1450)
    const scale = Math.max(0.75, maxWidth / baseViewport.width)
    const viewport = page.getViewport({ scale, rotation })

    if (token !== renderTokenRef.current) return

    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)

    const context = canvas.getContext("2d")
    if (!context) {
      setError("No se pudo inicializar el lienzo para renderizar el PDF.")
      return
    }

    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    canvas.style.transform = "none"

    setRendering(true)
    try {
      const task = page.render({ canvasContext: context, viewport })
      renderTaskRef.current = task
      await task.promise
    } catch (err) {
      if (!isRenderCancelledError(err)) {
        setError("Ocurrio un error al renderizar la pagina del PDF.")
      }
    } finally {
      if (token === renderTokenRef.current) {
        setRendering(false)
        renderTaskRef.current = null
      }
    }
  }, [isFullscreen, pageNumber, pdfDoc])

  useEffect(() => {
    void renderPage()
  }, [renderPage])

  useEffect(() => {
    if (!pdfDoc) return
    const onResize = () => void renderPage()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [pdfDoc, renderPage])

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener("fullscreenchange", onFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange)
  }, [])

  useEffect(() => {
    return () => {
      renderTokenRef.current += 1
      if (renderTaskRef.current?.cancel) {
        renderTaskRef.current.cancel()
      }
    }
  }, [])

  const toggleFullscreen = async () => {
    if (!viewerRef.current) return
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }
    await viewerRef.current.requestFullscreen()
  }

  const goPrev = () => setPageNumber((p) => Math.max(1, p - 1))
  const goNext = () => setPageNumber((p) => Math.min(numPages, p + 1))
  const isPrevDisabled = loading || rendering || pageNumber <= 1
  const isNextDisabled = loading || rendering || pageNumber >= numPages

  return (
    <div>
      {(title || description) && (
        <header className="mb-6 text-center">
          {title && (
            <div className="mt-8 flex justify-center">
              <SectionTitle text={title} variant="light" className="text-4xl md:text-5xl" />
            </div>
          )}
          {description && (
            <p className="mt-1 text-sm text-zinc-600 md:text-base">{description}</p>
          )}
        </header>
      )}

      <section
        ref={viewerRef}
        className={`rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm md:p-6 ${
          isFullscreen ? "!h-full !w-full !max-w-none !rounded-none !border-0 !p-2 md:!p-3 flex flex-col bg-zinc-900" : ""
        }`}
      >
        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>
        ) : (
          <div className={`overflow-auto rounded-xl border border-zinc-200 bg-zinc-50 p-2 md:p-3 ${isFullscreen ? "flex-1" : ""}`}>
            <canvas ref={canvasRef} className="mx-auto h-auto max-w-full rounded bg-white shadow" />
          </div>
        )}

        <div className={`mt-5 flex flex-col items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 md:p-4 ${isFullscreen ? "mt-2 shrink-0" : ""}`}>
          <div className="flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row">
            <button
              type="button"
              onClick={goPrev}
              disabled={isPrevDisabled}
              style={{ backgroundColor: isPrevDisabled ? "#f99ea1" : "#fb0009" }}
              className="w-full rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 disabled:cursor-not-allowed sm:w-auto"
            >
              Anterior
            </button>

            <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm ring-1 sm:w-auto">
              <label className="text-sm font-medium text-zinc-700" htmlFor="page-input">
                Pagina
              </label>
              <input
                id="page-input"
                type="number"
                min={1}
                max={Math.max(numPages, 1)}
                value={pageNumber}
                onChange={(e) => {
                  const value = Number(e.target.value)
                  if (Number.isNaN(value)) return
                  setPageNumber(Math.min(Math.max(value, 1), Math.max(numPages, 1)))
                }}
                className="w-16 rounded-md border border-gray-300 px-2 py-1 text-center text-sm font-semibold text-zinc-900"
              />
              <span className="rounded-md bg-zinc-100 px-2 py-1 text-sm text-zinc-600">
                de {numPages || "..."}
              </span>
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={isNextDisabled}
              style={{ backgroundColor: isNextDisabled ? "#f99ea1" : "#fb0009" }}
              className="w-full rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 disabled:cursor-not-allowed sm:w-auto"
            >
              Siguiente
            </button>

            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-800 shadow-sm transition hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 sm:w-auto"
            >
              {isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            </button>
          </div>

          {(loading || rendering) && (
            <span className="text-sm text-zinc-500">Cargando pagina...</span>
          )}
        </div>
      </section>
    </div>
  )
}
